import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Send, Loader2, User, Smartphone, MessageSquare, ArrowRight, CheckCircle2 } from "lucide-react";

// ================= INTERNAL CSS =================
const styles = `
  body{
  
  }
  .animate-up { animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; transform: translateY(30px); }
  
  @keyframes slideUp {
    to { opacity: 1; transform: translateY(0); }
  }

  /* The Main Floating Card */
  .contact-wrapper {
    background: #fff;
    border-radius: 20px;
    box-shadow: 0 20px 60px -10px rgba(0, 0, 0, 0.15);
    overflow: hidden;
  }

  /* Left Side (Dark Info Panel) */
  .info-panel {
    background: linear-gradient(145deg, #1e293b, #0f172a);
    color: white;
    position: relative;
    overflow: hidden;
  }
  
  /* Decorative Circles in Dark Panel */
  .circle-decor {
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.05);
    z-index: 0;
  }

  /* Right Side (Form) */
  .form-input {
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    padding: 14px 16px;
    border-radius: 8px;
    font-size: 0.95rem;
    transition: all 0.2s ease;
  }
  .form-input:focus {
    background-color: #fff;
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
    outline: none;
  }
  .form-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 6px;
    display: block;
  }

  /* Hover Effects for Info Items */
  .info-item {
    transition: transform 0.2s;
    cursor: default;
  }
  .info-item:hover {
    transform: translateX(5px);
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({ name: "", mobile: "", email: "", message: "" });
  const [status, setStatus] = useState("idle"); 

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const submitHandler = (e) => {
    e.preventDefault();
    setStatus("loading");
    axios.post(`https://ezer-eventapp.onrender.com/enquiries`, formData)
      .then(() => {
        setStatus("success");
        setFormData({ name: "", mobile: "", email: "", message: "" });
        setTimeout(() => setStatus("idle"), 4000);
      })
      .catch((err) => {
        console.error(err);
        setStatus("error");
      });
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center mt-4 py-5" style={{ backgroundColor:" #020617" }}>
      <style>{styles}</style>

      <div className="container">
        
        {/* Page Title */}
        <div className="text-center mb-5 animate-up">
          <h1 className="fw-bold text-white display-5" style={{ animationDelay: "0.1s", margin:"15px"}}>Contact Us</h1>
          <p className="text-white">Any questions or remarks? Just write us a message!</p>
        </div>

        {/* MAIN CARD WRAPPER */}
        <div className="contact-wrapper row g-0 animate-up" style={{ animationDelay: "0.1s" }}>
          
          {/* LEFT SIDE: Contact Information (Dark Theme) */}
          <div className="col-lg-5 info-panel p-5 d-flex flex-column justify-content-between">
            {/* Background Decoration */}
            <div className="circle-decor" style={{ width: 200, height: 200, bottom: -50, right: -50 }}></div>
            <div className="circle-decor" style={{ width: 100, height: 100, top: 50, right: 50 }}></div>

            <div className="position-relative z-1">
              <h3 className="fw-bold mb-4">Contact Information</h3>
              <p className="opacity-75 mb-5">Fill up the form and our Team will get back to you within 24 hours.</p>

              <div className="d-flex flex-column gap-4">
                <div className="d-flex align-items-center gap-3 info-item">
                  <div className="p-2 rounded bg-white bg-opacity-10"><Phone size={20} className="text-info" /></div>
                  <span className="fs-6">+91 98765 43210</span>
                </div>
                
                <div className="d-flex align-items-center gap-3 info-item">
                  <div className="p-2 rounded bg-white bg-opacity-10"><Mail size={20} className="text-warning" /></div>
                  <span className="fs-6">support@ezerbooking.com</span>
                </div>
                
                <div className="d-flex align-items-center gap-3 info-item">
                  <div className="p-2 rounded bg-white bg-opacity-10"><MapPin size={20} className="text-danger" /></div>
                  <span className="fs-6">123 Market Street, Hyderabad</span>
                </div>
              </div>
            </div>

            {/* Embed Map inside the dark panel (Small & Stylized) */}
            <div className="mt-5 position-relative z-1 rounded-3 overflow-hidden shadow-sm" style={{ height: "180px", opacity: 0.9 }}>
              <iframe
                title="map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3628.079405796066!2d78.4421420749486!3d17.435309583460032!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb910036466fe3%3A0xaaffe29685bc02bc!2sAmeerpet%20metro%20station!5e1!3m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0, filter: "invert(90%) hue-rotate(180deg)" }}
                allowFullScreen=""
                loading="lazy"
              ></iframe>
            </div>
          </div>

          {/* RIGHT SIDE: Form (Light Theme) */}
          <div className="col-lg-7 bg-white p-5">
            <form onSubmit={submitHandler} className="h-100 d-flex flex-column justify-content-center">
              
              <div className="row g-4">
                {/* Name */}
                <div className="col-md-6">
                  <label className="form-label">Your Name</label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0 border text-muted" style={{borderTopLeftRadius: 8, borderBottomLeftRadius: 8}}><User size={18}/></span>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} className="form-control form-input border-start-0 ps-0" style={{borderTopLeftRadius: 0, borderBottomLeftRadius: 0}} placeholder="John Doe" required />
                  </div>
                </div>

                {/* Mobile */}
                <div className="col-md-6">
                  <label className="form-label">Phone Number</label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0 border text-muted" style={{borderTopLeftRadius: 8, borderBottomLeftRadius: 8}}><Smartphone size={18}/></span>
                    <input type="tel" name="mobile" value={formData.mobile} onChange={handleChange} className="form-control form-input border-start-0 ps-0" style={{borderTopLeftRadius: 0, borderBottomLeftRadius: 0}} placeholder="+91 98..." required />
                  </div>
                </div>

                {/* Email */}
                <div className="col-12">
                  <label className="form-label">Email Address</label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0 border text-muted" style={{borderTopLeftRadius: 8, borderBottomLeftRadius: 8}}><Mail size={18}/></span>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} className="form-control form-input border-start-0 ps-0" style={{borderTopLeftRadius: 0, borderBottomLeftRadius: 0}} placeholder="you@company.com" required />
                  </div>
                </div>

                {/* Message */}
                <div className="col-12">
                  <label className="form-label">Message</label>
                  <div className="position-relative">
                    <textarea name="message" value={formData.message} onChange={handleChange} className="form-control form-input" rows="4" placeholder="Write your message here..." required></textarea>
                    <MessageSquare size={18} className="text-muted position-absolute top-0 end-0 mt-3 me-3 opacity-50"/>
                  </div>
                </div>
              </div>

              {/* Submit Button Area */}
              <div className="mt-5 d-flex justify-content-end align-items-center">
                {status === "error" && <span className="text-danger small me-3 fw-bold">Failed to send. Try again.</span>}
                
                <button 
                  type="submit" 
                  disabled={status === "loading" || status === "success"}
                  className={`btn px-5 py-3 rounded-3 fw-bold d-flex align-items-center gap-2 transition-all ${
                    status === "success" ? "btn-success" : "btn-dark"
                  }`}
                  style={{ minWidth: "180px", justifyContent: "center" }}
                >
                  {status === "loading" ? (
                    <> <Loader2 className="animate-spin" size={20} /> Sending... </>
                  ) : status === "success" ? (
                    <> <CheckCircle2 size={20} /> Sent </>
                  ) : (
                    <> Send Message <ArrowRight size={18} /> </>
                  )}
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;